/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { t } from 'i18next';
import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { routerActions } from '@opengeoweb/store';
import { workspaceActions, workspaceRoutes } from '@opengeoweb/workspace';

export const RouteChangeListener: FC<{
  initialWorkspaceId: string | undefined;
}> = ({ initialWorkspaceId }) => {
  const dispatch = useDispatch();

  const navigateBackOrForward = useCallback(
    (workspaceId: string): void => {
      dispatch(routerActions.navigateBackOrForward({ workspaceId }));
    },
    [dispatch],
  );

  const changeToEmptyMapWorkspace = useCallback((): void => {
    dispatch(
      workspaceActions.changeToEmptyMapWorkspace({
        newMapPresetText: t('workspace-mappreset-new'),
        newWorkspaceText: t('workspace-new'),
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    const handlePopState = (): void => {
      const { pathname } = window.location;
      const workspaceId = pathname.split('/workspace/')[1];

      if (!workspaceId) {
        if (initialWorkspaceId && pathname !== workspaceRoutes.workspace) {
          navigateBackOrForward(initialWorkspaceId);
        } else {
          changeToEmptyMapWorkspace();
        }
      } else {
        navigateBackOrForward(workspaceId);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return (): void => window.removeEventListener('popstate', handlePopState);
  }, [changeToEmptyMapWorkspace, initialWorkspaceId, navigateBackOrForward]);

  return null;
};
